import * as React from 'react';
import { createContext, useContext, useState, useEffect, useCallback, useRef, ReactNode } from 'react';
import authService from '../services/authService';
import cookieSSOService from '../services/cookieSSOService';
import { User } from 'oidc-client-ts';

// Define types for the auth context
interface AuthContextType {
  user: User | null;
  loading: boolean;
  error: Error | null;
  signin: () => Promise<void>;
  signout: () => Promise<void>;
  handleSignInCallback: () => Promise<User>;
  isAuthenticated: () => Promise<boolean>;
  getUserProfile: () => Promise<any>;
  getAccessToken: () => Promise<string | null>;
  redirectReady: boolean;
  redirectPath: string | null;
  setRedirectReady: React.Dispatch<React.SetStateAction<boolean>>;
  setRedirectPath: React.Dispatch<React.SetStateAction<string | null>>;
}

// Define props for the AuthProvider component
interface AuthProviderProps {
  children: ReactNode;
}

// Create the authentication context with null as default value
const AuthContext = createContext<AuthContextType | null>(null);

// Custom hook to use the auth context
export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

// Authentication provider component
export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<Error | null>(null);
  const [isRefreshing, setIsRefreshing] = useState<boolean>(false);
  const [redirectReady, setRedirectReady] = useState<boolean>(false);
  const [redirectPath, setRedirectPath] = useState<string | null>(null);
  
  // Check if authentication is disabled via environment variable
  const isAuthDisabled = process.env.REACT_APP_DISABLE_AUTH === 'true';
  
  // Ref to prevent multiple simultaneous auth operations
  const isHandlingAuthRef = useRef<boolean>(false);

  // Function to save username and email to sessionStorage
  const saveUserInfoToSessionStorage = useCallback((user: User | null): void => {
    if (user && user.profile) {
      let username = null;

      // First try preferred_username if it exists and doesn't look like an email
      if (user.profile.preferred_username && !user.profile.preferred_username.includes('@')) {
        username = user.profile.preferred_username;
      }
      // If no preferred_username or it looks like an email, try name if it doesn't look like an email
      else if (user.profile.name && !user.profile.name.includes('@')) {
        username = user.profile.name;
      }
      // Only use sub as last resort if it doesn't look like an email
      else if (user.profile.sub && !user.profile.sub.includes('@')) {
        username = user.profile.sub;
      }

      if (username) {
        sessionStorage.setItem('username', username);
      } else {
        sessionStorage.removeItem('username');
      }

      // Store email to sessionStorage
      if (user.profile.email) {
        sessionStorage.setItem('userEmail', user.profile.email);
      } else {
        sessionStorage.removeItem('userEmail');
      }
    } else {
      // Clear username and email from sessionStorage if user is null
      sessionStorage.removeItem('username');
      sessionStorage.removeItem('userEmail');
    }
  }, []);

  // Function to load the user - extracted for reuse
  const loadUser = useCallback(async (): Promise<void> => {
    try {
      setLoading(true);

      localStorage.removeItem('sso_authenticated');

      // STEP 1: Try SSO cookie detection first (cross-app authentication)
      try {
        const ssoToken = cookieSSOService.detectSSOCookie();
        if (ssoToken) {
          console.log('SSO cookie detected, attempting to establish session');
          const ssoUser = await cookieSSOService.exchangeSSOCookieForSession();
          if (ssoUser) {
            console.log('SSO cookie authentication successful');
            setUser(ssoUser);
            saveUserInfoToSessionStorage(ssoUser);
            // Set SSO cookie with current token for other apps
            if (ssoUser.access_token) {
              cookieSSOService.setSSOCookie(ssoUser.access_token, ssoUser.expires_in);
            }

            // Only mark SSO authentication if we're on root path or signin page
            // This prevents the flag from being set repeatedly on every page
            const currentPath = window.location.pathname;
            if (currentPath === '/' || currentPath === '/signin' || currentPath === '/selectorg') {
              localStorage.setItem('sso_authenticated', 'true');

              // Store the referrer URL (where user came from - likely Workplace app)
              const referrer = document.referrer;
              if (referrer && referrer !== window.location.href) {
                sessionStorage.setItem('sso_referrer_url', referrer);
                console.log('SSO: Stored referrer URL:', referrer);
              }

              // Get organization_id from cookies (set by Workplace app)
              const orgCookie = document.cookie
                .split(';')
                .find(c => c.trim().startsWith('organization_id='));

              if (orgCookie) {
                const orgId = decodeURIComponent(orgCookie.split('=')[1]);
                // Set organization in sessionStorage so DesignsList doesn't redirect
                sessionStorage.setItem('selectedOrgId', orgId);

                // Fetch the actual organization name and domain from the API
                try {
                  const orgInfo = await cookieSSOService.getOrganizationInfoForValidUser();
                  if (orgInfo) {
                    sessionStorage.setItem('selectedOrgName', orgInfo.name);
                    sessionStorage.setItem('selectedOrgDomain', orgInfo.domain);
                  } else {
                    // Fallback to default if fetch fails
                    sessionStorage.setItem('selectedOrgName', 'Unknown');
                    sessionStorage.setItem('selectedOrgDomain', 'unknown');
                  }
                } catch (orgFetchError) {
                  console.error('SSO: Error fetching organization info:', orgFetchError);
                  // Fallback to default if fetch fails
                  sessionStorage.setItem('selectedOrgName', 'Unknown');
                  sessionStorage.setItem('selectedOrgDomain', 'unknown');
                }
              }

              console.log('SSO authentication successful, marked in localStorage for redirect');
            } else {
              console.log('SSO authentication successful on page:', currentPath);
            }

            setLoading(false);
            return; // Successfully authenticated via SSO
          } else {
            console.log('SSO cookie authentication failed');
            // Clear invalid SSO cookies
            cookieSSOService.clearSSOCookie();
            // Check if referrer is from a DIFFERENT application (external, like Workplace)
            const referrer = document.referrer;
            const isExternalReferrer = referrer &&
              new URL(referrer).origin !== window.location.origin;

            if (isExternalReferrer) {
              console.log('SSO validation failed, redirecting back to external caller:', referrer);
              window.location.href = referrer;
              return; // Prevent further execution while redirecting
            }
            console.log('SSO validation failed, falling back to normal auth flow');
            // Continue to STEP 2 (auth code check) and STEP 3 (normal user load)
          }
        }
      } catch (ssoError) {
        console.log('SSO detection failed, falling back to normal flow:', ssoError);
      }

      // STEP 2: Check if we have auth code in sessionStorage
      const hasAuthCode = sessionStorage.getItem('auth_code') !== null;

      if (hasAuthCode) {
        try {
          // Process the authentication callback
          const callbackUser = await authService.handleSignInCallback();
          setUser(callbackUser);
          // Save username and email to sessionStorage
          saveUserInfoToSessionStorage(callbackUser);
          // Set SSO cookie for cross-app sharing
          if (callbackUser.access_token) {
            cookieSSOService.setSSOCookie(callbackUser.access_token, callbackUser.expires_in);
          }
          console.log('Authentication callback processed successfully');

          // Check if we have a redirect path stored in sessionStorage
          const storedRedirectPath = sessionStorage.getItem('auth_redirect_path');
          if (storedRedirectPath) {
            // Clear the stored path
            sessionStorage.removeItem('auth_redirect_path');
            // Set the redirect path and flag
            setRedirectPath(storedRedirectPath);
            setRedirectReady(true);
          }
        } catch (callbackError) {
          console.error('Error processing authentication callback:', callbackError);
          setError(callbackError instanceof Error ? callbackError : new Error(String(callbackError)));
          // Set user to null on error
          setUser(null);
          // Clear username and email from sessionStorage on error
          saveUserInfoToSessionStorage(null);
        }
      } else {
        // STEP 3: Just load the current user
        try {
          const currentUser = await authService.getUser();
          setUser(currentUser);
          // Save username and email to sessionStorage
          saveUserInfoToSessionStorage(currentUser);
          // Set SSO cookie if we have a user
          if (currentUser && currentUser.access_token) {
            cookieSSOService.setSSOCookie(currentUser.access_token, currentUser.expires_in);
          }
        } catch (getUserError) {
          console.error('Error getting current user:', getUserError);
          setUser(null);
          // Clear username and email from sessionStorage on error
          saveUserInfoToSessionStorage(null);
        }
      }
    } catch (err) {
      console.error('Error loading user:', err);
      setError(err instanceof Error ? err : new Error(String(err)));
      // Set user to null on error
      setUser(null);
      // Clear username and email from sessionStorage on error
      saveUserInfoToSessionStorage(null);
    } finally {
      // Always set loading to false
      setLoading(false);
    }
  }, [saveUserInfoToSessionStorage]);

  // Function to refresh the token
  const refreshToken = useCallback(async (): Promise<void> => {
    if (isRefreshing) return;
    
    try {
      setIsRefreshing(true);
      // The oidc-client-ts library handles token refresh internally
      // We just need to get the updated user
      const refreshedUser = await authService.getUser();
      if (refreshedUser && !refreshedUser.expired) {
        setUser(refreshedUser);
      } else if (user && !refreshedUser) {
        // User was logged out or token expired and couldn't be refreshed
        setUser(null);
      }
    } catch (err) {
      console.error('Error refreshing token:', err);
      setError(err instanceof Error ? err : new Error(String(err)));
    } finally {
      setIsRefreshing(false);
    }
  }, [isRefreshing, user]);

  // Load the user on component mount and handle authentication callback
  useEffect(() => {
    // If authentication is disabled, skip all auth setup
    if (isAuthDisabled) {
      setLoading(false);
      return;
    }
    
    // Function to handle page refresh cases
    const handlePageRefresh = async (): Promise<void> => {
      if (isHandlingAuthRef.current) {
        return;
      }
      
      try {
        isHandlingAuthRef.current = true;
        
        // First try to load the user normally
        await loadUser();
      } catch (loadError) {
        console.error('Error loading user on initial load:', loadError);
        
        try {
          // If loading fails (e.g., after a page refresh), try to get the current user directly
          const currentUser = await authService.getUser();
          if (currentUser && !currentUser.expired) {
            setUser(currentUser);
            // Save username and email to sessionStorage when user is recovered
            saveUserInfoToSessionStorage(currentUser);
          } else {
            // If no current user or expired, clear the user state
            setUser(null);
            // Clear username and email from sessionStorage
            saveUserInfoToSessionStorage(null);
          }
        } catch (getUserError) {
          console.error('Error getting current user:', getUserError);
          setUser(null);
        } finally {
          setLoading(false);
        }
      } finally {
        // Important: Set loading to false here to ensure we exit the loading state
        // even if there was an error in loadUser
        setLoading(false);
        
        // Reset the flag after a delay to prevent immediate re-triggering
        setTimeout(() => {
          isHandlingAuthRef.current = false;
        }, 1000);
      }
    };
    
    // Call the function to handle page refresh cases only once on mount
    handlePageRefresh();

    // Set up event listeners for token events
    const userLoadedHandler = (loadedUser: User): void => {
      setUser(loadedUser);
      // Save username and email to sessionStorage when user is loaded
      saveUserInfoToSessionStorage(loadedUser);
    };

    const userSignedOutHandler = (): void => {
      setUser(null);
      // Clear username and email from sessionStorage when user signs out
      saveUserInfoToSessionStorage(null);
    };

    const silentRenewErrorHandler = (err: Error): void => {
      console.error('Silent renew error:', err);
      // Try to refresh the token manually, but don't do it if we're already handling auth
      if (!isHandlingAuthRef.current) {
        refreshToken();
      }
    };

    // Add event listeners
    const userManager = authService.getUserManager();
    if (userManager) {
      userManager.events.addUserLoaded(userLoadedHandler);
      userManager.events.addUserSignedOut(userSignedOutHandler);
      userManager.events.addSilentRenewError(silentRenewErrorHandler);
    }

    // Set up a timer to periodically check for user changes
    // This is especially important with in-memory storage
    // as we need to refresh tokens before they expire
    const checkUserInterval = setInterval(async () => {
      // Skip if we're already handling auth
      if (isHandlingAuthRef.current) {
        return;
      }
      
      try {
        const currentUser = await authService.getUser();
        
        // If user exists but is expired or about to expire, refresh the token
        if (currentUser && currentUser.expires_in !== undefined && currentUser.expires_in < 60) {
          isHandlingAuthRef.current = true;
          await refreshToken();
          isHandlingAuthRef.current = false;
        } 
        // If user state has changed, update it
        // But only compare if both objects exist to avoid unnecessary updates
        else if (currentUser && user && JSON.stringify(currentUser) !== JSON.stringify(user)) {
          setUser(currentUser);
        }
        // If one exists and the other doesn't, update
        else if (!!currentUser !== !!user) {
          setUser(currentUser);
        }
      } catch (err) {
        console.error('Error checking user state:', err);
      }
    }, 30000); // Check every 30 seconds

    // Clean up interval and event listeners on unmount
    return () => {
      clearInterval(checkUserInterval);
      if (userManager) {
        userManager.events.removeUserLoaded(userLoadedHandler);
        userManager.events.removeUserSignedOut(userSignedOutHandler);
        userManager.events.removeSilentRenewError(silentRenewErrorHandler);
      }
    };
  }, [loadUser, refreshToken, user, isHandlingAuthRef, isAuthDisabled]);

  // Sign in function
  const signin = async (): Promise<void> => {
    try {
      await authService.signin();
    } catch (err) {
      console.error('Sign in error:', err);
      setError(err instanceof Error ? err : new Error(String(err)));
      throw err;
    }
  };

  // Sign out function
  const signout = async (): Promise<void> => {
    try {
      // Clear SSO cookie
      cookieSSOService.clearSSOCookie();
      await authService.signout();
    } catch (err) {
      console.error('Sign out error:', err);
      setError(err instanceof Error ? err : new Error(String(err)));
      throw err;
    }
  };

  // Handle the sign in callback
  const handleSignInCallback = async (): Promise<User> => {
    try {
      setLoading(true);
      const callbackUser = await authService.handleSignInCallback();
      if (!callbackUser) {
        throw new Error('Failed to get user from sign in callback');
      }
      setUser(callbackUser);
      // Save username and email to sessionStorage
      saveUserInfoToSessionStorage(callbackUser);
      return callbackUser;
    } catch (err) {
      console.error('Handle sign in callback error:', err);
      setError(err instanceof Error ? err : new Error(String(err)));
      throw err;
    } finally {
      setLoading(false);
    }
  };

  // Check if the user is authenticated
  const isAuthenticated = async (): Promise<boolean> => {
    // If authentication is disabled, always return true
    if (isAuthDisabled) {
      return true;
    }
    return await authService.isAuthenticated();
  };

  // Get the user profile
  const getUserProfile = async (): Promise<any> => {
    return await authService.getUserProfile();
  };

  // Get the access token
  const getAccessToken = async (): Promise<string | null> => {
    return await authService.getAccessToken();
  };

  // Context value
  const contextValue: AuthContextType = {
    user,
    loading,
    error,
    signin,
    signout,
    handleSignInCallback,
    isAuthenticated,
    getUserProfile,
    getAccessToken,
    // Expose redirect state and setters
    redirectReady,
    redirectPath,
    setRedirectReady,
    setRedirectPath
  };

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
